<script setup lang="ts">
/**
 * What either party can do to a single lesson.
 *
 * Students and tutors see the same two choices and reach the same endpoints —
 * the difference is only in what they cost, and the server decides that. The one
 * thing this must never do is let somebody cancel without first being told the
 * price. A tutor cancelling must tell the family why.
 */
import { ref, computed } from 'vue'
import { Button } from '@/Components/ui/button'
import { CalendarClock, XCircle, AlertCircle } from 'lucide-vue-next'
import { sendPayingIfNeeded } from '@/lib/lessonPayment'
import { getLessonTimezone } from '@/lib/lessonTime'

interface Lesson {
  id: number
  subscription_id: number
  scheduled_at: string
  status: string
  /** False once a request is already waiting on this lesson: it cannot take a second. */
  can_change: boolean
}

const props = defineProps<{
  lesson: Lesson
  /** Tutors are never charged for cancelling; students may be. */
  isTutor?: boolean
}>()

/** `message` is the server's confirmation, when it gives one: the lesson leaves the list on reload, so the parent shows it. */
const emit = defineEmits<{ changed: [message?: string] }>()

type Panel = 'none' | 'cancel' | 'move'

const panel = ref<Panel>('none')
const busy = ref(false)
const error = ref('')
const moveTo = ref('')
const message = ref('')
const quote = ref<{
  fee_percentage: number
  fee_amount: number
  is_free: boolean
  /** The moment the lesson was due to be charged: a paid lesson moved further out keeps it. */
  free_until: string
  tutor_credit_owed: number
} | null>(null)

// The fee is judged from when the lesson was due to be charged, not from how far
// away it is now, so the explanation names that moment rather than a 48-hour rule.
const freeUntil = computed(() =>
  quote.value
    ? new Date(quote.value.free_until).toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: getLessonTimezone(),
      })
    : ''
)

const heading = computed(() => {
  if (!quote.value) return ''
  if (props.isTutor) {
    return quote.value.tutor_credit_owed
      ? `Your student will not be charged, and anything they paid goes back to them. Free cancellation ended on ${freeUntil.value} UK time, so it counts as a warning, and the £${quote.value.tutor_credit_owed.toFixed(2)} credit your student receives comes out of your next payout.`
      : 'Your student will not be charged for this lesson, and anything they paid goes back to them.'
  }

  return quote.value.is_free
    ? 'You will not be charged for this lesson.'
    : `Free cancellation ended on ${freeUntil.value} UK time, so you will be charged the full £${quote.value.fee_amount.toFixed(2)}.`
})

async function openCancel() {
  error.value = ''
  panel.value = 'cancel'
  quote.value = null

  try {
    const { data } = await window.axios.get(`/lesson-changes/lessons/${props.lesson.id}/cancellation-quote`)
    quote.value = data
  } catch {
    error.value = 'We could not work out what this would cost. Please try again.'
  }
}

async function send(request: () => Promise<any>) {
  busy.value = true
  error.value = ''

  try {
    const response = await request()
    close()
    emit('changed', response?.data?.message)
  } catch (failure: any) {
    error.value =
      failure?.response?.data?.message ??
      Object.values(failure?.response?.data?.errors ?? {}).flat()[0] ??
      'That did not go through. Please try again.'
  } finally {
    busy.value = false
  }
}

const confirmCancel = () =>
  send(() => window.axios.post(`/lesson-changes/lessons/${props.lesson.id}/cancel`, { reason: message.value || null }))

const proposeMove = () =>
  send(() =>
    sendPayingIfNeeded(props.lesson.subscription_id, paymentIntentId =>
      window.axios.post(`/lesson-changes/lessons/${props.lesson.id}/propose`, {
        type: 'reschedule',
        scheduled_at: moveTo.value,
        message: message.value || null,
        payment_intent_id: paymentIntentId,
      })
    )
  )

function close() {
  panel.value = 'none'
  moveTo.value = ''
  message.value = ''
  quote.value = null
}
</script>

<template>
  <div class="space-y-3">
    <div v-if="panel === 'none'" class="flex flex-wrap items-center gap-2">
      <!-- Moving needs agreement, so only one request can be outstanding at a
           time. Cancelling needs nobody's, so it stays available regardless. -->
      <Button v-if="lesson.can_change" variant="outline" size="sm" @click="panel = 'move'">
        <CalendarClock class="mr-1 h-4 w-4" />
        Move
      </Button>
      <span v-else class="text-xs text-gray-500">A request on this lesson is waiting for an answer.</span>

      <Button variant="ghost" size="sm" class="text-red-600 hover:text-red-700" @click="openCancel">
        <XCircle class="mr-1 h-4 w-4" />
        Cancel
      </Button>
    </div>

    <div v-else class="rounded-lg border border-gray-200 p-4 space-y-3">
      <p v-if="error" class="flex items-start gap-2 text-sm text-red-700">
        <AlertCircle class="mt-0.5 h-4 w-4 shrink-0" />
        {{ error }}
      </p>

      <!-- Moving is a request. Saying so prevents the commonest misreading:
           that asking to move a lesson has already moved it. -->
      <template v-if="panel === 'move'">
        <p class="text-sm font-medium text-gray-900">Ask to move this lesson</p>
        <p class="text-xs text-gray-500">
          Your {{ isTutor ? 'student' : 'tutor' }} has to agree. Until they do, the lesson stays as it is.
        </p>
        <label class="block text-xs text-gray-700">
          New date and time, UK time
          <input v-model="moveTo" type="datetime-local" class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
        </label>
      </template>

      <template v-else>
        <p class="text-sm font-medium text-gray-900">Cancel this lesson</p>
        <p v-if="!quote" class="text-xs text-gray-500">Working out what this costs…</p>
        <p v-else class="text-xs" :class="(isTutor ? !quote.tutor_credit_owed : quote.is_free) ? 'text-gray-500' : 'text-amber-700'">
          {{ heading }}
        </p>
        <p class="text-xs text-gray-500">This cannot be undone.</p>
      </template>

      <textarea
        v-if="panel === 'move' || isTutor"
        v-model="message"
        rows="2"
        :placeholder="panel === 'cancel' ? 'Tell your student why (required)' : 'Add a note (optional)'"
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
      ></textarea>

      <div class="flex gap-2">
        <Button
          v-if="panel === 'move'"
          size="sm"
          :disabled="busy || !moveTo"
          @click="proposeMove"
        >
          {{ busy ? 'Sending…' : 'Send request' }}
        </Button>
        <Button
          v-else
          size="sm"
          variant="destructive"
          :disabled="busy || !quote || (isTutor && !message.trim())"
          @click="confirmCancel"
        >
          {{ busy ? 'Cancelling…' : 'Cancel the lesson' }}
        </Button>

        <Button variant="ghost" size="sm" :disabled="busy" @click="close">Back</Button>
      </div>
    </div>
  </div>
</template>
